import Config from './Config';
import { MakeApiCallAsync } from './ApiHelpers';

const CACHE_KEY = 'ukl-home-catalog-v1';
const CACHE_AGE = 15 * 60 * 1000;
const parameters = { SearchTerm: null, SizeID: '', ColorID: null, CategoryID: '', TagID: '', ManufacturerID: '',
    MinPrice: null, MaxPrice: null, Rating: null, OrderByColumnName: '', PageSize: 100, recordValueJson: '[]' };

export function normalizeProducts(products) {
    const byId = new Map();
    for (const product of products) {
        if (!product || typeof product !== 'object') continue;
        const id = Number(product.ProductId ?? product.ProductID);
        if (!Number.isInteger(id) || id <= 0) continue;
        let images = product.ProductImagesJson;
        try { if (typeof images === 'string') images = JSON.parse(images); } catch { images = []; }
        byId.set(id, { ...product, ProductId: id, ProductImagesJson: Array.isArray(images) ? images.filter(Boolean) : [] });
    }
    return [...byId.values()].sort((a, b) => b.ProductId - a.ProductId);
}

export function readCatalogCache() {
    try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
        if (cached && Date.now() - cached.savedAt < CACHE_AGE && Array.isArray(cached.products)) return normalizeProducts(cached.products);
    } catch { /* Storage is optional. */ }
    return [];
}

async function page(pageNo) {
    for (let attempt = 0; attempt < 3; attempt++) {
        try {
            const response = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_All_PRODUCTS, null,
                { requestParameters: { ...parameters, PageNo: pageNo } },
                { Accept: 'application/json', 'Content-Type': 'application/json' }, 'POST', false, { timeout: 12000 });
            if (response?.status !== 200 || response.data?.success === false || response.data?.isSuccess === false) throw new Error('Catalog unavailable');
            const value = response.data?.data;
            const products = typeof value === 'string' ? JSON.parse(value) : value;
            if (!Array.isArray(products)) throw new Error('Invalid catalog response');
            // Retry transient empty responses before declaring the catalog empty.
            if (!products.length && attempt < 2) throw new Error('Empty catalog response');
            return products;
        } catch (error) {
            if (attempt === 2) throw error;
            await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
    }
}

export async function loadHomeCatalog(onProgress = () => {}) {
    let products = await page(1);
    if (!products.length && readCatalogCache().length) throw new Error('Empty refresh; retaining the last complete catalog');
    onProgress(normalizeProducts(products));
    const total = Number(products[0]?.TotalRecords) || products.length;
    const pageCount = Math.ceil(total / parameters.PageSize);
    let nextPage = 2;
    const failures = [];
    await Promise.all(Array.from({ length: Math.min(3, Math.max(0, pageCount - 1)) }, async () => {
        while (nextPage <= pageCount) {
            const number = nextPage++;
            try {
                const batch = await page(number);
                if (!batch.length) throw new Error('Missing catalog page');
                products = [...products, ...batch];
                onProgress(normalizeProducts(products));
            } catch (error) { failures.push(error); }
        }
    }));
    if (failures.length) throw new Error('Some products could not be refreshed');
    const result = normalizeProducts(products);
    if (products.length && !result.length) throw new Error('Invalid catalog products');
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), products: result })); } catch { /* Storage may be full. */ }
    return result;
}
